import {
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { Component as BrComponent, Page } from '@bloomreach/spa-sdk';
import {
  AnalyticsService,
  CustomDimensions,
  MarketoFormConfig,
} from '@frk/eds-components';
import { MarketoPreferencesResult } from '@frk/eds-components/lib/models/marketo-form.model';
import { LOCATION } from '@ng-web-apis/common';
import { IUserProfile } from '@services/profile.interface';
import { ProfileService } from '@services/profile.service';
import { Logger } from '@utils/logger';
import { noop, Subject, throwError } from 'rxjs';
import { catchError, filter, takeUntil } from 'rxjs/operators';
import { UserPreferencesService } from './user-preferences.service';

const logger = Logger.getLogger('MarketoFormComponent');

@Component({
  selector: 'ft-marketo-form',
  templateUrl: './marketo-form.component.html',
  styleUrls: ['./marketo-form.component.scss'],
})
export class MarketoFormComponent implements OnInit, OnDestroy, OnChanges {
  /**
   * The Bloomreach component
   */
  @Input() component!: BrComponent;

  /**
   * The Bloomreach page
   */
  @Input() page!: Page;

  /**
   * Marketo Form Id from parent component
   */
  @Input() externalId: string;

  /**
   * Success title from parent component
   */
  @Input() externalSuccessTitle: string;

  /**
   * Success message from parent component
   */
  @Input() externalSuccessMessage: string;

  /**
   * Margin bottom from parent component
   */
  @Input() externalMarginBottom: string;

  /**
   * Marketo configuration
   */
  formConfig: MarketoFormConfig = {
    marketoJSSrc: '/assets/js/marketo/forms2.min.js',
    marketoUrl: '//pages.to.franklintempleton.com',
    munchkinId: '848-IAP-939',
  };

  /**
   * User subscription data for preferences
   */
  userSubscriptionData?: MarketoPreferencesResult;

  /**
   * used to remove subscriptions when component is destroyed
   */
  private unsubscribe$: Subject<void> = new Subject<void>();

  preferencesLoaded: boolean;

  /**
   * Constructor
   */
  constructor(
    private analytics: AnalyticsService,
    private router: Router,
    private profileService: ProfileService,
    private userPreferencesService: UserPreferencesService,
    private changeDetectorRef: ChangeDetectorRef,
    @Inject(LOCATION) readonly locationRef: Location
  ) {}

  /**
   * Ng On Init
   */
  ngOnInit(): void {
    logger.debug('Marketo Form ngOnInit');
    this.profileService
      .getUserProfile$()
      ?.pipe(
        takeUntil(this.unsubscribe$),
        filter((profile: IUserProfile): boolean => {
          if (profile.isLoggedIn) {
            return !!profile?.profileInfo?.email;
          } else {
            return true;
          }
        })
      )
      .subscribe((profileData: IUserProfile): void => {
        this.userPreferencesService
          .getEmailPreferences$(profileData?.profileInfo?.email)
          .pipe(
            takeUntil(this.unsubscribe$),
            catchError((err) => {
              this.preferencesLoaded = true;
              this.changeDetectorRef.detectChanges();
              return [];
            })
          )
          .subscribe(
            (data: MarketoPreferencesResult) => {
              if (data?.result?.length > 0) {
                // Agreed approach: Use the record with not empty or the highest number ID.
                // Service should return results always as an array & front-end component can sort/filter results on "id".
                const sortedResults = data.result.sort((a, b) => b.id - a.id);
                this.userSubscriptionData = data;
                this.userSubscriptionData.result[0] = sortedResults[0];
              }
            },
            noop,
            () => {
              this.preferencesLoaded = true;
              this.changeDetectorRef.detectChanges();
            }
          );
      });
  }

  ngOnChanges(): void {}
  /**
   * Ng On Destroy
   */
  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
  /**
   * Get Marketo Form Id
   */
  get id(): string {
    if (this.externalId) {
      return this.externalId;
    }
    return this.component.getParameters()?.id;
    // return '865'; //email subscription MK2 form ID
  }

  /**
   * Get Marketo Success Title
   */
  get successTitle(): string {
    if (this.externalSuccessTitle) {
      return;
    }
    return this.component.getParameters()?.successTitle;
  }
  /**
   * Get Marketo Success Msg
   */
  get successMsg(): string {
    if (this.externalSuccessMessage) {
      return this.externalSuccessMessage;
    }
    return this.component.getParameters()?.successMsg;
  }

  /**
   * Get Margin Bottom parameter
   */
  get marginBottom(): string {
    if (this.externalMarginBottom) {
      return this.externalMarginBottom;
    }
    const marginBottom: string = this.component.getParameters()?.marginBottom;
    return marginBottom;
  }

  /**
   * Get Marketo unsubscribe button flag
   */
  get hasUnsubscribeButton(): boolean {
    return this.component?.getParameters()?.hasUnsubscribeButton;
  }
  /**
   * Get Marketo unsubscribe button text
   */
  get unsubscribeButtonText(): string {
    return this.component?.getParameters()?.unsubscribeButtonText;
  }

  /**
   * Get Marketo Success Url
   */
  get successUrl(): string {
    return this.component.getParameters()?.successUrl;
  }

  /**
   * Get Retain Form After Submission value
   */
  get retainFormAfterSubmission(): boolean {
    const isFormRetained: boolean = this.component?.getParameters()
      ?.retainFormAfterSubmission
      ? this.component.getParameters()?.retainFormAfterSubmission
      : false;
    return isFormRetained;
  }

  /**
   * Analytics tracker for form
   */
  formAnalytics(): void {
    this.analytics.trackEvent({
      event: 'leadformSubmit',
      LeadFormAction: `mktoForm_${this.id}`,
      LeadFormCategory: 'Lead',
      LeadFormCheckbox: '',
      LeadFormLabel: 'Submit',
      LeadFormURL: this.router.url,
      LeadFormID: this.id,
    });
  }
}
